import React from 'react';

interface OnboardingShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Wider card, for a two-column choice or a step with a code block. */
  wide?: boolean;
  onBack?: () => void;
}

/**
 * The full-screen card every pre-dashboard page sits in.
 *
 * Shared because there were already three hand-rolled copies of it — OnboardingPage kept this
 * privately while AwaitingApprovalPage and AgentAuthorizePage each repeated the same
 * `min-h-screen` centred card by eye, and the first-run flow would have been the fourth. They had
 * already drifted apart in padding and max-width.
 *
 * These routes are registered flat, outside IdentityGate and AppShell, so there is no layout above
 * them — this is it.
 */
export function OnboardingShell({
  title,
  subtitle,
  children,
  wide = false,
  onBack,
}: OnboardingShellProps) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-4">
      {/* The same corner web the signed-in shell uses, so sign-up already looks like the product rather
          than a form that happens to precede it. Masked to fade out, or it competes with the content. */}
      <img
        src="/web.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none fixed -left-10 -top-10 h-[760px] w-[760px] select-none opacity-[0.10]"
        style={{
          maskImage: 'radial-gradient(circle at 0% 0%, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle at 0% 0%, black 20%, transparent 70%)',
        }}
      />

      <div
        className={`relative z-10 w-full rounded-xl border border-border bg-surface p-8 ${wide ? 'max-w-2xl' : 'max-w-md'}`}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-4 text-sm text-text-secondary hover:text-text-primary"
          >
            &larr; Back
          </button>
        )}
        <h1 className="mb-2 text-2xl font-semibold text-text-primary">{title}</h1>
        {subtitle && <p className="mb-6 text-sm text-text-secondary">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}
